import { useState } from 'react'

import { useIsFreeUser } from '~publish/contexts/AccountContext'

import type {
  PostType,
  SortDirection,
  TagComparator,
  TopPostsSortBy,
} from '../graphqlEnums'

import { useChannelAnalyticsExport } from './useChannelAnalyticsExport'
import type { InsightsExportConfigInput } from './useChannelAnalyticsExport'
import { useInsightsExportUpgrade } from './useInsightsExportUpgrade'

export interface UseInsightsExportActionInput {
  organizationId: string
  channelIds: string[]
  canExport: boolean
  startDate: string
  endDate: string
  tags?: TagComparator
  /** Post formats to include. An empty list means every format. */
  postTypes?: readonly PostType[]
  postCount?: number
  /** Mirrors the All Channels table's active sort (multi-channel exports). */
  channelSort?: { metric: string; direction: SortDirection }
  /** Mirrors the single-channel page's active Top Posts sort. */
  postSort?: { field: TopPostsSortBy; direction: SortDirection }
  /** Post-metric columns (display order) from the Top Posts column picker. */
  postColumns?: string[]
  /** Channel-metric columns (display order) from the performance-table picker. */
  channelColumns?: string[]
}

interface UseInsightsExportActionOptions {
  /** When provided, called instead of the internal upgrade dialog. */
  onUpgradeRequired?: () => void
  /**
   * When provided, a PDF selection opens this configuration step (section
   * toggles + logo) instead of exporting immediately. The consumer owns the
   * dialog and calls `exportWithConfig` on confirm. CSV/Markdown are never
   * intercepted. Only invoked after the free-user upgrade gate passes.
   */
  onConfigurePdf?: () => void
}

interface UseInsightsExportActionResult {
  showExport: boolean
  showUpgradePath: boolean
  isExporting: boolean
  isLongRunning: boolean
  handleSelect: (format: 'csv' | 'markdown' | 'pdf') => void
  /** Runs a PDF export with the config chosen in the dialog. */
  exportWithConfig: (config: InsightsExportConfigInput) => void
  isUpgradeDialogOpen: boolean
  onUpgradeDialogOpenChange: (open: boolean) => void
  handleUpgradeClick: () => void
}

export function useInsightsExportAction(
  input: UseInsightsExportActionInput,
  options: UseInsightsExportActionOptions = {},
): UseInsightsExportActionResult {
  const {
    organizationId,
    channelIds,
    canExport,
    startDate,
    endDate,
    tags,
    postTypes,
    postCount,
    channelSort,
    postSort,
    postColumns,
    channelColumns,
  } = input
  const { onUpgradeRequired, onConfigurePdf } = options

  const isFreeUser = useIsFreeUser()

  // A free user keeps the menu, but every selection meets the upgrade dialog.
  const showUpgradePath = canExport && isFreeUser
  const showExport = canExport

  const { exportData, isExporting, isLongRunning } = useChannelAnalyticsExport({
    organizationId,
    channelIds,
    startDate,
    endDate,
    tags,
    postTypes,
    postCount,
    channelSort,
    postSort,
    postColumns,
    channelColumns,
  })
  const { handleExportBlocked } = useInsightsExportUpgrade()
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)

  const handleSelect = (format: 'csv' | 'markdown' | 'pdf'): void => {
    // Guard against a stale click delivered after the permission is revoked
    if (!showExport) return
    if (showUpgradePath) {
      // Prefer the externally-owned dialog so state survives responsive layout
      // switches; fall back to internal state for standalone usage.
      if (onUpgradeRequired) {
        onUpgradeRequired()
      } else {
        setIsUpgradeDialogOpen(true)
      }
      return
    }
    // PDF opens the section/logo configuration step when the consumer provides
    // a handler; otherwise (and for CSV/Markdown) it exports immediately.
    if (format === 'pdf' && onConfigurePdf) {
      onConfigurePdf()
      return
    }
    exportData(format)
  }

  // Runs the PDF export with the configuration collected by the dialog. The
  // upgrade gate has already been cleared (the dialog only opens past it).
  const exportWithConfig = (config: InsightsExportConfigInput): void => {
    if (!showExport) return
    exportData('pdf', config)
  }

  const handleUpgradeClick = (): void => {
    setIsUpgradeDialogOpen(false)
    handleExportBlocked()
  }

  return {
    showExport,
    showUpgradePath,
    isExporting,
    isLongRunning,
    handleSelect,
    exportWithConfig,
    isUpgradeDialogOpen,
    onUpgradeDialogOpenChange: setIsUpgradeDialogOpen,
    handleUpgradeClick,
  }
}
